import { useContext, useState } from 'react';
import { VStack } from '@chakra-ui/react';
import { Icon, IconButton, MenuRow, Popover } from '../../ui';
import type { SelectionPart, SelectionTabKey } from '../../hooks/useGetOrderDetail';
import SessionContext from '../../contexts/SessionContext';

const EXCLUDABLE_PHASES = new Set<SelectionTabKey>(['pending', 'excluded', 'no-quoted']);

const selectionPartCanBeMarkedOrdered = (
    phaseKey: SelectionTabKey,
    part: Pick<SelectionPart, 'status' | 'selectionStatus'>,
): boolean => {
    if (part.status === 'confirmed' || part.status === 'ordered' || part.selectionStatus === 'ordered') return false;
    if (phaseKey === 'excluded' || part.selectionStatus === 'excluded' || part.status === 'selection-excluded') return false;
    if (phaseKey === 'pending' || phaseKey === 'no-quoted') return true;
    return part.status === 'selection' || part.status === 'no-quoted';
};

const selectionPartCanBeExcluded = (
    phaseKey: SelectionTabKey,
    part: Pick<SelectionPart, 'status' | 'selectionStatus'>,
): boolean => {
    if (part.status === 'confirmed' || part.status === 'ordered' || part.selectionStatus === 'ordered') return false;
    if (EXCLUDABLE_PHASES.has(phaseKey)) return true;
    if (phaseKey !== 'all') return false;
    return part.status === 'no-quoted'
        || part.status === 'selection'
        || part.status === 'selection-excluded'
        || part.status === 'selection-excluded-temporary'
        || part.selectionStatus === 'excluded';
};

export interface SelectionPartActionsMenuProps {
    phaseKey: SelectionTabKey;
    part: Pick<SelectionPart, 'status' | 'selectionStatus' | 'excludeOrdering' | 'canMarkUnordered'>;
    isEvOrder: boolean;
    onExclude: () => void;
    onMarkOrdered: () => void;
}

const SelectionPartActionsMenu = ({
    phaseKey,
    part,
    isEvOrder,
    onExclude,
    onMarkOrdered,
}: SelectionPartActionsMenuProps) => {
    const sessionContext = useContext(SessionContext);
    const [isOpen, setIsOpen] = useState(false);
    const allowPlaceOrder = (isEvOrder && sessionContext?.permissions?.includes('place-ev-order'))
        || !!sessionContext?.permissions?.includes('place-order');
    const canExclude = !!sessionContext?.permissions?.includes('include-exclude-parts')
        && selectionPartCanBeExcluded(phaseKey, part);
    const isExcluded = !!part.excludeOrdering;
    const isMarkedOrdered = part.status === 'confirmed' || part.status === 'ordered';
    const showMarkOrdered = allowPlaceOrder && selectionPartCanBeMarkedOrdered(phaseKey, part);
    const showMarkUnordered = allowPlaceOrder && isMarkedOrdered && part.canMarkUnordered;

    if (!canExclude && !showMarkOrdered && !showMarkUnordered) return null;

    const closeAnd = (action: () => void) => {
        setIsOpen(false);
        action();
    };

    return (
        <Popover
            isOpen={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            placement="bottom-end"
            width="max-content"
            trigger={
                <IconButton
                    aria-label="More options"
                    icon={<Icon name="dotsVertical" size="sm" />}
                    size="sm"
                    variant="ghost"
                    color="fg.muted"
                    onClick={(event) => event.stopPropagation()}
                />
            }
        >
            <VStack align="stretch" spacing={0} p={1}>
                {canExclude && (
                    <MenuRow
                        kind="single"
                        icon={<Icon name={isExcluded ? 'checkCircle' : 'circleOff'} size="sm" />}
                        onClick={() => closeAnd(onExclude)}
                    >
                        {isExcluded ? 'Include' : 'Exclude'}
                    </MenuRow>
                )}
                {(showMarkOrdered || showMarkUnordered) && (
                    <MenuRow
                        kind="single"
                        icon={<Icon name={showMarkUnordered ? 'xCircle' : 'checks'} size="sm" />}
                        onClick={() => closeAnd(onMarkOrdered)}
                    >
                        {showMarkUnordered ? 'Mark Unordered' : 'Mark Ordered'}
                    </MenuRow>
                )}
            </VStack>
        </Popover>
    );
};

export default SelectionPartActionsMenu;
